// src/pages/accounting/budget/BudgetYearlyDetail.jsx

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../auth";
import styles from "./BudgetYearlyDetail.module.css";

const API_DOC   = "/api/budget/documents/";
const API_SHEET = "/api/budget/sheets/";


const STATUS_BADGE = {
  draft:     { label: "작성중",   color: "#6c757d" },
  submitted: { label: "결재상신", color: "#0d6efd" },
  approved:  { label: "결재완료", color: "#198754" },
  locked:    { label: "잠금",    color: "#dc3545" },
  rejected:  { label: "반려",    color: "#fd7e14" },
};

// ────────────────────────────────────────────────
// ExcelGrid: grid_json → 스프레드시트 테이블
// ────────────────────────────────────────────────
function ExcelGrid({ gridJson }) {
  const { data = [], merges = [], col_widths = {} } = gridJson || {};

  // 병합셀 맵 생성
  const { mergeMap, skipSet } = useMemo(() => {
    const mm  = {};
    const sk  = new Set();
    merges.forEach(m => {
      mm[`${m.r}-${m.c}`] = { rs: m.rs, cs: m.cs };
      for (let r = m.r; r < m.r + m.rs; r++) {
        for (let c = m.c; c < m.c + m.cs; c++) {
          if (r !== m.r || c !== m.c) sk.add(`${r}-${c}`);
        }
      }
    });
    return { mergeMap: mm, skipSet: sk };
  }, [merges]);

  // 열 개수
  const colCount = useMemo(
    () => data.reduce((max, row) => Math.max(max, row.length), 0),
    [data],
  );

  // 열 너비 계산 (pt → px, 기본 70px)
  const getColWidth = idx => {
    const pt = col_widths[String(idx)];
    if (!pt) return "70px";
    return `${Math.round(pt * 7)}px`;
  };

  if (data.length === 0) {
    return <p className={styles.emptyGrid}>시트 데이터가 없습니다.</p>;
  }

  return (
    <div className={styles.gridWrap}>
      <table className={styles.gridTable}>
        {/* colgroup으로 열 너비 설정 */}
        <colgroup>
          <col style={{ width: "36px" }} /> {/* 행 번호 */}
          {Array.from({ length: colCount }, (_, i) => (
            <col key={i} style={{ width: getColWidth(i) }} />
          ))}
        </colgroup>

        {/* 열 헤더 (A, B, C …) */}
        <thead>
          <tr>
            <th className={styles.thCorner} />
            {Array.from({ length: colCount }, (_, i) => (
              <th key={i} className={styles.thCol}>
                {colIndexToLetter(i)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, rIdx) => (
            <tr key={rIdx}>
              {/* 행 번호 */}
              <td className={styles.tdRowNum}>{rIdx + 1}</td>

              {Array.from({ length: colCount }, (_, cIdx) => {
                const key   = `${rIdx}-${cIdx}`;
                if (skipSet.has(key)) return null;
                const merge = mergeMap[key];
                const cell  = row[cIdx] ?? { v: "" };
                const v     = cell.v !== null && cell.v !== undefined ? cell.v : "";

                return (
                  <td
                    key={cIdx}
                    rowSpan={merge?.rs || 1}
                    colSpan={merge?.cs || 1}
                    className={styles.tdCell}
                    title={String(v)}
                  >
                    {formatCellValue(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 열 인덱스 → 엑셀 알파벳 (0→A, 25→Z, 26→AA …)
function colIndexToLetter(idx) {
  let result = "";
  let n = idx;
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

// 숫자 3자리 콤마, 긴 텍스트 말줄임
function formatCellValue(v) {
  if (typeof v === "number") {
    return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
  }
  return String(v);
}

// ────────────────────────────────────────────────
// 메인 페이지
// ────────────────────────────────────────────────
export default function BudgetYearlyDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [doc,           setDoc]           = useState(null);
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [sheetDetail,   setSheetDetail]   = useState(null);  // grid_json 포함
  const [loading,       setLoading]       = useState(true);
  const [sheetLoading,  setSheetLoading]  = useState(false);
  const [error,         setError]         = useState("");

  // 문서 + 시트 목록 불러오기
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_DOC}${id}/`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (!res.ok) throw new Error(`서버 오류 (${res.status})`);
        const data = await res.json();
        setDoc(data);
        // 첫 번째 시트 자동 선택
        if (data.sheets?.length > 0) {
          setActiveSheetId(data.sheets[0].id);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // 활성 시트 상세(grid_json) 불러오기
  const loadSheet = useCallback(async (sheetId) => {
    setSheetLoading(true);
    try {

      const res = await fetch(`${API_SHEET}${sheetId}/`, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });


      if (!res.ok) throw new Error(`시트 로딩 실패 (${res.status})`);
      const data = await res.json();
      setSheetDetail(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSheetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSheetId) loadSheet(activeSheetId);
  }, [activeSheetId, loadSheet]);

  if (loading) return <p className={styles.stateMsg}>불러오는 중…</p>;
  if (error)   return <p className={styles.errorMsg}>{error}</p>;
  if (!doc)    return null;

  const badge = STATUS_BADGE[doc.status] ?? { label: doc.status, color: "#888" };

  return (
    <div className={styles.wrap}>
      {/* 상단 정보 바 */}
      <div className={styles.topBar}>
        <div className={styles.topLeft}>
          <button
            className={styles.btnBack}
            onClick={() => navigate("/accounting/budget/yearly/list")}
          >
            ← 목록
          </button>
          <div>
            <h2 className={styles.docTitle}>{doc.title}</h2>
            <div className={styles.docMeta}>
              <span>{doc.fiscal_year}년</span>
              <span className={styles.dot}>·</span>
              <span>{doc.business_unit_display}</span>
              <span className={styles.dot}>·</span>
              <span
                className={styles.badge}
                style={{ background: badge.color }}
              >
                {badge.label}
              </span>
              {doc.description && (
                <>
                  <span className={styles.dot}>·</span>
                  <span className={styles.desc}>{doc.description}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.topRight}>
          <span className={styles.metaSmall}>
            작성: {doc.created_at?.slice(0, 10)}
          </span>
          {doc.is_editable && (
            <button
              className={styles.btnEdit}
              onClick={() => alert("편집 기능은 추후 지원 예정입니다.")}
            >
              편집
            </button>
          )}
        </div>
      </div>

      {/* 시트 탭 */}
      {doc.sheets?.length > 0 && (
        <div className={styles.sheetTabs}>
          {doc.sheets.map(sheet => (
            <button
              key={sheet.id}
              className={`${styles.sheetTab} ${
                activeSheetId === sheet.id ? styles.sheetTabActive : ""
              }`}
              onClick={() => setActiveSheetId(sheet.id)}
            >
              {sheet.sheet_name}
              <span className={styles.sheetMeta}>
                {sheet.row_count}행 × {sheet.col_count}열
              </span>
            </button>
          ))}
        </div>
      )}

      {/* 그리드 */}
      <div className={styles.gridArea}>
        {sheetLoading ? (
          <p className={styles.stateMsg}>시트 로딩 중…</p>
        ) : sheetDetail ? (
          <>
            <div className={styles.gridInfo}>
              <span className={styles.sheetName}>{sheetDetail.sheet_name}</span>
              <span className={styles.gridSize}>
                {sheetDetail.row_count}행 × {sheetDetail.col_count}열
              </span>
            </div>
            <ExcelGrid gridJson={sheetDetail.grid_json} />
          </>
        ) : (
          <p className={styles.stateMsg}>시트를 선택하세요.</p>
        )}
      </div>
    </div>
  );
}